import { useEffect, useRef, useState } from 'react'

/** What the connection indicator shows (T8): the stream is up, or it is not. */
export type LiveStatus = 'connecting' | 'live' | 'down'

/** The hint stream of one branch (G5): `/api/public/<r>/<b>/events`. */
export function branchEventsUrl(restaurantSlug: string, branchSlug: string) {
  return `/api/public/${encodeURIComponent(restaurantSlug)}/${encodeURIComponent(branchSlug)}/events`
}

/** Backoff for re-creating a stream the server refused (a 429 at capacity, a 5xx page). */
const RETRY_MIN_MS = 5_000
const RETRY_MAX_MS = 60_000

interface Stream {
  pause: () => void
  resume: () => void
}

/** Every stream open on the page, so `printPage` can close them around the dialog. */
const streams = new Set<Stream>()

/** How long a print dialog that never says `afterprint` (a no-op `print()` in an in-app browser) keeps the streams closed. */
const PRINT_RESUME_MS = 5_000

/** WebKit defers `print()` while an open EventSource keeps the document loading: close the streams for the dialog, reopen behind it. */
export function printPage() {
  for (const stream of streams) stream.pause()
  const resume = () => {
    clearTimeout(fallback)
    window.removeEventListener('afterprint', resume)
    streams.forEach((stream) => stream.resume())
  }
  window.addEventListener('afterprint', resume)
  const fallback = setTimeout(resume, PRINT_RESUME_MS)
  window.print()
}

/**
 * Subscribes to a branch's hint stream and calls `onChange` for every hint
 * and for every reconnect — a gap may have swallowed hints, so reconnect =
 * refetch (T7). Hints carry no data: `onChange` invalidates queries, nothing
 * else. Callers keep a slow `refetchInterval` as the fallback; the stream only
 * makes updates prompt. `undefined` (no branch yet) opens nothing.
 */
export function useBranchEvents(url: string | undefined, onChange: () => void): LiveStatus {
  // Keyed by URL so a switch reads as connecting until the new stream reports.
  const [state, setState] = useState<{ url: string; status: LiveStatus }>()
  const latest = useRef(onChange)
  useEffect(() => {
    latest.current = onChange
  }, [onChange])

  const supported = typeof EventSource !== 'undefined'
  useEffect(() => {
    if (!url || !supported) return
    let source: EventSource | undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    let delay = RETRY_MIN_MS
    let everOpened = false
    // Same status again (each native retry fires `error`) is not a render.
    const report = (status: LiveStatus) =>
      setState((current) => (current?.url === url && current.status === status ? current : { url, status }))

    const connect = () => {
      source = new EventSource(url)
      source.onopen = () => {
        delay = RETRY_MIN_MS
        report('live')
        // The first open follows the page's own initial load; later ones follow a gap.
        if (everOpened) latest.current()
        everOpened = true
      }
      source.addEventListener('changed', () => latest.current())
      source.onerror = () => {
        report('down')
        // CONNECTING: the browser retries by itself. CLOSED: the server refused
        // the stream (429 at capacity, a dead slug), so the browser gave up — we retry.
        if (source?.readyState === EventSource.CLOSED) {
          source = undefined
          timer = setTimeout(connect, delay)
          delay = Math.min(delay * 2, RETRY_MAX_MS)
        }
      }
    }
    connect()

    const stream: Stream = {
      pause: () => {
        clearTimeout(timer)
        source?.close()
        source = undefined
        report('down')
      },
      resume: () => {
        if (!source) connect()
      },
    }
    streams.add(stream)

    return () => {
      streams.delete(stream)
      stream.pause()
    }
  }, [url, supported])

  if (!url || !supported) return 'down'
  return state?.url === url ? state.status : 'connecting'
}
